"use client";

import * as React from "react";
import { ArrowRight, Blocks, CircleCheck as CheckCircle2, ChevronRight, Clock, Compass, Cpu, GraduationCap, CircleQuestionMark as HelpCircle, Info, Layers, Lightbulb, MessageSquare, MonitorOff, MoveRight, Presentation, Rocket, RotateCw, SlidersHorizontal as Sliders, Sparkles, Tablet, Tag, Target, Trophy, Users, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface TranslationStrings {
  breadcrumbHome: string;
  breadcrumbPathway: string;
  breadcrumbTrack: string;
  breadcrumbCurrent: string;
  pathwayBadge: string;
  courseCode: string;
  courseTitle: string;
  ageLabel: string;
  ageValue: string;
  platformLabel: string;
  platformValue: string;
  durationLabel: string;
  durationValue: string;
  groupLabel: string;
  groupValue: string;
  description: string;
  ctaConsultation: string;
  ctaPathway: string;
  targetAudienceTitle: string;
  targetAudienceSubtitle: string;
  targetAudienceAge: string;
  targetAudiencePrereq: string;
  targetAudienceDesc: string;
  targetAudiencePoints: Array<{
    title: string;
    desc: string;
  }>;
  unpluggedTitle: string;
  unpluggedNote: string;
  timelineTitle: string;
  timelineSubtitle: string;
  timelinePhases: Array<{
    phase: string;
    sessions: string;
    title: string;
    focus: string;
    items: string[];
  }>;
  methodologyTitle: string;
  methodologySubtitle: string;
  methodologySteps: Array<{
    step: string;
    title: string;
    desc: string;
  }>;
  finalProjectTag: string;
  finalProjectTitle: string;
  finalProjectDesc: string;
  finalProjectStations: Array<{
    name: string;
    detail: string;
  }>;
  specTag: string;
  specTitle: string;
  specs: Array<{
    label: string;
    value: string;
  }>;
  equipmentTag: string;
  equipmentTitle: string;
  equipmentPlatformLabel: string;
  equipmentPlatformValue: string;
  equipmentCostLabel: string;
  equipmentCostValue: string;
  equipmentCostNote: string;
  equipmentComputerLabel: string;
  equipmentComputerValue: string;
  equipmentTabletLabel: string;
  equipmentTabletValue: string;
  smallGroupTag: string;
  smallGroupTitle: string;
  smallGroupIntro: string;
  smallGroupIdealBadge: string;
  smallGroupIdealTitle: string;
  smallGroupIdealDesc: string;
  smallGroupIdealPoints: string[];
  smallGroupCostBadge: string;
  smallGroupCostTitle: string;
  smallGroupCostDesc: string;
  smallGroupCostPoints: string[];
  roadmapTitle: string;
  roadmapSubtitle: string;
  currentBadge: string;
  skillsTitle: string;
  skillsDescription: string;
  firstCourseNotice: string;
  nextStepCardTitle: string;
  nextStepCardDesc: string;
  nextStepButton: string;
  nextCourseLabel: string;
  nextCourseButton: string;
  skillsList: Array<{
    name: string;
    desc: string;
  }>;
}
const contentData: Record<"vi" | "en", TranslationStrings> = {
  vi: {
    breadcrumbHome: "Trang chủ",
    breadcrumbPathway: "Lộ trình Robotics",
    breadcrumbTrack: "Robotics 4–6 tuổi",
    breadcrumbCurrent: "D1 – Bé làm kỹ sư",
    pathwayBadge: "Cấp độ Khởi đầu · Mầm non",
    courseCode: "D1",
    courseTitle: "Bé làm kỹ sư",
    ageLabel: "Độ tuổi",
    ageValue: "4–5 tuổi",
    platformLabel: "Bộ học cụ",
    platformValue: "LEGO® Duplo STEAM",
    durationLabel: "Thời lượng",
    durationValue: "12 buổi × 90 phút",
    groupLabel: "Quy mô nhóm",
    groupValue: "2–3 học sinh / bộ kit",
    description: "Làm quen với chi tiết lắp ráp, đọc hình theo từng bước, kết cấu vững và các chuyển động cơ bản.",
    ctaConsultation: "ĐĂNG KÝ TƯ VẤN",
    ctaPathway: "XEM TOÀN BỘ LỘ TRÌNH",
    targetAudienceTitle: "Khóa học này dành cho ai?",
    targetAudienceSubtitle: "Cánh cửa đầu tiên dẫn lối tư duy công nghệ & cơ học chuẩn quốc tế cho trẻ mầm non",
    targetAudienceAge: "Độ tuổi phù hợp: 4–5 tuổi (Mầm non / Tiền tiểu học)",
    targetAudiencePrereq: "Yêu cầu đầu vào: Không cần kiến thức hay kỹ năng trước đó",
    targetAudienceDesc: "D1 là điểm khởi đầu chính thức của toàn bộ Lộ trình Robotics Foundation. Khóa học được tinh chỉnh để bồi dưỡng tư duy kỹ sư, khả năng xây dựng mô hình khối lớn và tư duy logic không màn hình (unplugged logic) trước khi bước sang các cấp độ robot lập trình vi mạch.",
    targetAudiencePoints: [{
      title: "Khởi đầu không áp lực",
      desc: "Không yêu cầu trẻ biết đọc chữ hay thao tác máy tính; tiếp cận hoàn toàn qua trực quan vật lý."
    }, {
      title: "Tư duy kỹ sư tạo hình",
      desc: "Hình thành khả năng giải mã không gian, nhận biết tỷ lệ và nguyên lý cân bằng hình học."
    }, {
      title: "Bước đệm cho lập trình",
      desc: "Xây nền thuật toán thông qua chỉ dẫn biểu tượng mũi tên, tư duy thứ tự bước đi và sửa lỗi thực tế."
    }],
    unpluggedTitle: "Phương pháp Lập trình Không màn hình (Unplugged)",
    unpluggedNote: "Lưu ý: Tư duy lập trình ở cấp độ này được xây dựng hoàn toàn không qua màn hình máy tính. Trẻ rèn luyện tư duy thuật toán thông qua thẻ mũi tên trực quan, chuỗi hành động thực tế và thao tác trực quan tay - mắt.",
    timelineTitle: "Cấu trúc 12 buổi học",
    timelineSubtitle: "Lộ trình phân tầng 5 giai đoạn từ nhận biết căn bản đến chế tác và báo cáo kỹ thuật",
    timelinePhases: [{
      phase: "Giai đoạn 1",
      sessions: "Buổi 1 – 2",
      title: "Khởi động & Khám phá vật liệu",
      focus: "Làm quen thế giới kỹ thuật và phân loại chi tiết",
      items: ["Quy tắc an toàn tại Lab Robotics & thao tác với khối gạch Duplo STEAM.", "Phân loại hình khối, màu sắc và khớp nối cơ bản."]
    }, {
      phase: "Giai đoạn 2",
      sessions: "Buổi 3 – 6",
      title: "Xây dựng: Lắp ráp & Kết cấu vững",
      focus: "Lắp ráp theo hình, kết cấu vững & chuyển động",
      items: ["Đọc sơ đồ trực quan 2D chuyển hóa thành công trình 3D.", "Thực hành nguyên lý chân đế rộng, khóa gạch và giằng chịu lực.", "Ứng dụng chuyển động quay (trục, bánh răng) và chuyển động lăn (bánh xe, dốc nghiêng)."]
    }, {
      phase: "Giai đoạn 3",
      sessions: "Buổi 7 – 9",
      title: "Tư duy thuật toán (Unplugged Logic)",
      focus: "Thẻ mũi tên, quy trình tuần tự, lặp lại & debugging",
      items: ["Điều hướng mô hình qua bản đồ bằng bộ thẻ lệnh mũi tên (Tiến - Lùi - Rẽ).", "Nhận diện quy luật lặp lại (vòng lặp sơ khai) trong chuyển động.", "Tìm và sửa lỗi đơn giản (Debugging): nhận biết sai khớp hoặc mất thăng bằng."]
    }, {
      phase: "Giai đoạn 4",
      sessions: "Buổi 10 – 11",
      title: "Thử nghiệm & Hoàn thiện",
      focus: "Tối ưu hóa cơ chế tải trọng và thử nghiệm độ bền",
      items: ["Thử thách tải trọng tĩnh và kiểm tra độ bền vững khi vận hành.", "Cải tiến kết cấu và nâng cấp chi tiết trang trí cơ học."]
    }, {
      phase: "Giai đoạn 5",
      sessions: "Buổi 12",
      title: "Trình bày sản phẩm & Lễ hội Kỹ sư",
      focus: "Thuyết trình, minh họa chức năng & nhận chứng chỉ",
      items: ["Bé tự tin giới thiệu công trình trước thầy cô và phụ huynh.", "Trải nghiệm tương tác tại các trạm Ngày hội Kỹ sư nhí."]
    }],
    methodologyTitle: "Phương pháp học 4 bước",
    methodologySubtitle: "Quy trình sư phạm chuẩn STEAM quốc tế giúp trẻ chủ động tiếp thu và chuyển hóa kiến thức",
    methodologySteps: [{
      step: "01",
      title: "Khởi động",
      desc: "Đặt vấn đề qua câu chuyện kỹ thuật thực tế, kích thích trí tò mò và khám phá hiện tượng vật lý."
    }, {
      step: "02",
      title: "Lắp ráp",
      desc: "Thao tác tay - mắt chính xác, đọc sơ đồ trực quan và xây dựng mô hình với kết cấu vững chắc."
    }, {
      step: "03",
      title: "Tư duy lập trình (Unplugged)",
      desc: "Thiết lập chuỗi mệnh lệnh bằng thẻ bài biểu tượng, rèn luyện tư duy thứ tự và sửa sai không cần màn hình."
    }, {
      step: "04",
      title: "Trình bày sản phẩm",
      desc: "Tập dượt kỹ năng thuyết trình, diễn giải cơ chế hoạt động của mô hình và tự hào chia sẻ kết quả."
    }],
    finalProjectTag: "Dự án / sản phẩm cuối khóa",
    finalProjectTitle: "Ngày hội Kỹ sư nhí (Junior Engineer Festival)",
    finalProjectDesc: "Sự kiện tổng kết nơi mỗi học viên trở thành một kỹ sư nhí thực thụ, vận hành các trạm trải nghiệm tương tác với sự tham gia cổ vũ của phụ huynh:",
    finalProjectStations: [{
      name: "Trạm Kết Cấu Vững (Structural Stability Station)",
      detail: "Thử thách xây tháp và cầu chịu lực với gạch Duplo, kiểm tra độ cân bằng khi chịu tải trọng mô phỏng."
    }, {
      name: "Trạm Chuyển Động Cơ Bản (Basic Motion Station)",
      detail: "Vận hành xe trượt dốc nghiêng và cối xoay truyền động bánh răng, quan sát tốc độ và quán tính chuyển động."
    }, {
      name: "Trạm Mê Cung Thuật Toán (Unplugged Maze Station)",
      detail: "Xếp thẻ mũi tên chỉ dẫn đường đi chính xác để đưa robot mô hình vượt qua các chướng ngại vật về đích an toàn."
    }],
    specTag: "Nền tảng / thiết bị",
    specTitle: "Thông số Kỹ thuật & Học cụ",
    specs: [{
      label: "Nền tảng chính (Platform)",
      value: "LEGO® Duplo STEAM Foundation Kit"
    }, {
      label: "Kích thước linh kiện",
      value: "Gạch kích thước lớn (Duplo standard) an toàn tuyệt đối"
    }, {
      label: "Cơ chế chuyển động",
      value: "Bánh răng đồng trục, trục xoay, bánh xe, khớp bản lề"
    }, {
      label: "Phương thức lập trình",
      value: "Unplugged 100% (Bộ thẻ lệnh định hướng & bản đồ sa bàn)"
    }, {
      label: "Độ an toàn vật liệu",
      value: "Nhựa ABS tiêu chuẩn an toàn cho trẻ mầm non quốc tế"
    }, {
      label: "Tỉ lệ thiết bị",
      value: "1 bộ kit tiêu chuẩn / 2–3 học sinh kèm giáo cụ riêng"
    }],
    equipmentTag: "Trang thiết bị & Nền tảng",
    equipmentTitle: "Thiết bị & Nền tảng học tập",
    equipmentPlatformLabel: "Nền tảng học cụ",
    equipmentPlatformValue: "Duplo (LEGO® Education)",
    equipmentCostLabel: "Chi phí thiết bị tham khảo",
    equipmentCostValue: "Khoảng 1.000.000 VNĐ / bộ kit",
    equipmentCostNote: "Lưu ý: Đây là chi phí thiết bị tham khảo và có thể thay đổi tùy thời điểm — khoản chi này hoàn toàn không phải học phí.",
    equipmentComputerLabel: "Máy tính",
    equipmentComputerValue: "KHÔNG yêu cầu máy tính",
    equipmentTabletLabel: "Thiết bị bổ trợ",
    equipmentTabletValue: "Máy tính bảng có thể được sử dụng tùy chọn cho các hoạt động tương tác bổ trợ — không bắt buộc đối với chương trình Duplo.",
    smallGroupTag: "Quy chuẩn lớp học",
    smallGroupTitle: "HỌC ROBOTICS THEO NHÓM NHỎ",
    smallGroupIntro: "ROBOTO tổ chức mô hình học tập theo nhóm nhỏ để đảm bảo mọi học sinh đều có đủ thời lượng trực tiếp thao tác lắp ráp, thử nghiệm, lập trình và tự tin thuyết trình sản phẩm.",
    smallGroupIdealBadge: "Mô hình lý tưởng",
    smallGroupIdealTitle: "2 học sinh / bộ kit",
    smallGroupIdealDesc: "Tối đa hóa thời lượng tương tác trực tiếp với học cụ và hỗ trợ phối hợp nhóm sâu sắc.",
    smallGroupIdealPoints: ["Nhiều thời lượng thực hành trực tiếp hơn cho từng học sinh", "Phối hợp làm việc nhóm thuận lợi, phân chia vai trò rõ ràng", "Thực hành sâu sắc qua từng vòng lắp ghép và thử nghiệm"],
    smallGroupCostBadge: "Mô hình tối ưu chi phí",
    smallGroupCostTitle: "3 học sinh / bộ kit",
    smallGroupCostDesc: "Duy trì môi trường hợp tác nhóm năng động đồng thời tối ưu hóa chi phí thiết bị học tập.",
    smallGroupCostPoints: ["Vẫn bảo đảm phương thức học tập tương tác theo nhóm", "Tối ưu hóa chi phí trang bị kit học cụ trên mỗi học viên", "Cân bằng hài hòa hiệu quả tổ chức và không gian lớp học"],
    roadmapTitle: "Vị trí trong lộ trình Robotics",
    roadmapSubtitle: "Chặng đầu tiên trong chuỗi 10 cấp độ đào tạo liên tục từ Mầm non đến Chuyên sâu thi đấu quốc tế",
    currentBadge: "Đang học (Current)",
    skillsTitle: "Kỹ năng trọng tâm phát triển",
    skillsDescription: "Chương trình chuẩn hóa phát triển đồng thời tư duy kỹ thuật vật lý và nền tảng tư duy tính toán:",
    firstCourseNotice: "Khóa học mở đầu của toàn bộ lộ trình Robotics ROBOTO",
    nextStepCardTitle: "Bước tiếp theo của con là gì?",
    nextStepCardDesc: "Sau khi hoàn thành D1, trẻ đã vững vàng kỹ năng đọc hình và liên kết khối. Bước chuyển tiếp tự nhiên và tối ưu nhất chính là D2 – Thành phố của em, nơi trẻ mở rộng sang tổ hợp công trình đô thị và các cơ cấu chuyển động phức hợp.",
    nextStepButton: "Xem chi tiết khóa học D2 →",
    nextCourseLabel: "Khóa học tiếp theo trong lộ trình:",
    nextCourseButton: "D2 — Thành phố của em →",
    skillsList: [{
      name: "Lắp ráp theo hình",
      desc: "Đọc sơ đồ phân rã trực quan từng bước và nhận biết hình học không gian."
    }, {
      name: "Kết cấu vững",
      desc: "Thực hành nguyên lý giằng chéo, chân đế cân bằng và chịu lực."
    }, {
      name: "Chuyển động quay",
      desc: "Khám phá trục, bánh răng và cơ chế xoay tròn đồng trục."
    }, {
      name: "Chuyển động lăn",
      desc: "Trải nghiệm bánh xe, mặt phẳng nghiêng và độ ma sát bề mặt."
    }, {
      name: "Gập mở & Bản lề",
      desc: "Lắp ráp cánh cửa, cầu nâng và khớp nối cơ học cơ bản."
    }, {
      name: "Tư duy thứ tự",
      desc: "Xác định rõ ràng bước nào cần làm trước, bước nào làm sau."
    }, {
      name: "Thẻ mũi tên định hướng",
      desc: "Mã hóa hướng đi (tiến, lùi, rẽ trái, rẽ phải) bằng thẻ thẻ bài biểu tượng."
    }, {
      name: "Lặp lại (Vòng lặp sơ khai)",
      desc: "Nhận biết các mẫu chuyển động lặp đi lặp lại trong tự nhiên và máy móc."
    }, {
      name: "Tìm và sửa lỗi đơn giản",
      desc: "Phát hiện mảnh ghép lắp sai khớp hoặc mô hình mất thăng bằng để chỉnh sửa."
    }]
  },
  en: {
    breadcrumbHome: "Home",
    breadcrumbPathway: "Robotics Pathway",
    breadcrumbTrack: "Robotics 4–6 yrs",
    breadcrumbCurrent: "D1 – Little Engineer",
    pathwayBadge: "Foundational Tier · Early Years",
    courseCode: "D1",
    courseTitle: "Little Engineer",
    ageLabel: "Target Age",
    ageValue: "4–5 years old",
    platformLabel: "Learning Platform",
    platformValue: "LEGO® Duplo STEAM",
    durationLabel: "Duration",
    durationValue: "12 sessions × 90 mins",
    groupLabel: "Class Ratio",
    groupValue: "2–3 students / kit",
    description: "Get familiar with building elements, step-by-step visual guides, stable structural integrity, and fundamental mechanical motions.",
    ctaConsultation: "BOOK CONSULTATION",
    ctaPathway: "VIEW FULL PATHWAY",
    targetAudienceTitle: "Who is this course for?",
    targetAudienceSubtitle: "The premier gateway to early engineering and international STEAM methodology for preschool learners",
    targetAudienceAge: "Target Age: 4–5 years old (Preschool / Kindergarten)",
    targetAudiencePrereq: "Prerequisites: null required (entry-level foundation)",
    targetAudienceDesc: "D1 is the official foundational entry point for the entire ROBOTO Robotics Pathway. Designed with zero screen exposure, it introduces foundational engineering mindset, large-brick building, and unplugged algorithmic logic before children encounter programmable robotics.",
    targetAudiencePoints: [{
      title: "Frictionless First Step",
      desc: "No prior literacy or computer operation required; 100% tangible, tactile exploration."
    }, {
      title: "Early Engineering Intuition",
      desc: "Builds spatial deconstruction skills, geometric proportions, and structural balance instincts."
    }, {
      title: "Pre-Coding Stepping Stone",
      desc: "Instills algorithm fundamentals via physical directional arrow cards, execution sequence, and calm debugging."
    }],
    unpluggedTitle: "Screen-Free Algorithmic Thinking (Unplugged Coding)",
    unpluggedNote: "Note: Computational thinking at this introductory stage is strictly screen-free. Children internalize sequence logic, directional arrow cards, and cause-and-effect reasoning through tangible manipulatives.",
    timelineTitle: "12-Session Course Structure",
    timelineSubtitle: "A structured 5-phase syllabus advancing from tactile familiarity to mechanical builds and presentation",
    timelinePhases: [{
      phase: "Phase 1",
      sessions: "Sessions 1 – 2",
      title: "Warm-up & Material Exploration",
      focus: "Engineering lab orientation and brick classification",
      items: ["Lab safety guidelines and handling LEGO® Duplo STEAM components.", "Categorizing shapes, vibrant colors, and fundamental interlocking studs."]
    }, {
      phase: "Phase 2",
      sessions: "Sessions 3 – 6",
      title: "Building: Assembly & Structural Integrity",
      focus: "Pictorial guides, structural stability, and basic motions",
      items: ["Translating 2D visual instructions into sturdy 3D physical structures.", "Practicing wide support bases, brick overlap locking, and vertical load-bearing.", "Hands-on rotational dynamics (axles, gears) and rolling mechanics (wheels, ramps)."]
    }, {
      phase: "Phase 3",
      sessions: "Sessions 7 – 9",
      title: "Algorithmic Thinking (Unplugged Logic)",
      focus: "Directional arrow cards, sequences, loops, and simple debugging",
      items: ["Navigating models on a playmat using symbolic command cards (Forward, Backward, Turns).", "Identifying repeating mechanical rhythms (early loop abstraction).", "Simple physical debugging: recognizing loose studs or off-balance pivots."]
    }, {
      phase: "Phase 4",
      sessions: "Sessions 10 – 11",
      title: "Testing & Refinement",
      focus: "Load-bearing optimization and movement stress tests",
      items: ["Static payload challenges and testing structural durability during movement.", "Iterating physical designs and refining modular mechanical attachments."]
    }, {
      phase: "Phase 5",
      sessions: "Session 12",
      title: "Showcase & Junior Engineer Festival",
      focus: "Demonstration, peer review, and certificate awards",
      items: ["Students present their custom models to peers, mentors, and visiting parents.", "Interactive hands-on stations at the Junior Engineer Festival celebration."]
    }],
    methodologyTitle: "4-Step Learning Methodology",
    methodologySubtitle: "An active, internationally recognized STEAM pedagogical loop calibrated for early childhood mastery",
    methodologySteps: [{
      step: "01",
      title: "Warm-up",
      desc: "Frame real-world challenges through relatable narratives, igniting curiosity about physical laws."
    }, {
      step: "02",
      title: "Assembly",
      desc: "Refine fine motor dexterity and spatial translation by assembling models with verified structural stability."
    }, {
      step: "03",
      title: "Unplugged Logic",
      desc: "Formulate command sequences with physical arrow cards, learning order and testing without monitors."
    }, {
      step: "04",
      title: "Presentation",
      desc: "Articulate how the model operates, develop technical vocabulary, and build early public confidence."
    }],
    finalProjectTag: "Final Capstone Project",
    finalProjectTitle: "Junior Engineer Festival",
    finalProjectDesc: "A celebratory capstone event where each young learner operates experiential engineering stations with family participation:",
    finalProjectStations: [{
      name: "Structural Stability Station",
      detail: "Construct resilient towers and weight-bearing bridges with Duplo bricks, evaluating stability under simulated loads."
    }, {
      name: "Basic Motion Station",
      detail: "Operate ramp vehicles and interconnected gear assemblies, observing how momentum and friction interact."
    }, {
      name: "Unplugged Maze Station",
      detail: "Layout sequenced directional arrow cards to guide physical builds through maze pathways to target checkpoints."
    }],
    specTag: "Platform / Equipment",
    specTitle: "Technical Specification Sheet",
    specs: [{
      label: "Core Platform",
      value: "LEGO® Duplo STEAM Foundation Kit"
    }, {
      label: "Component Scale",
      value: "Oversized, child-safe bricks certified for early learners"
    }, {
      label: "Mechanical Systems",
      value: "Spur gears, rotating axles, rolling wheels, and hinge joints"
    }, {
      label: "Programming Medium",
      value: "100% Unplugged (Physical direction cards & thematic mat boards)"
    }, {
      label: "Safety Standard",
      value: "Non-toxic food-grade ABS resin meeting international standards"
    }, {
      label: "Lab Allocation Ratio",
      value: "1 dedicated hardware kit per 2–3 students with custom mats"
    }],
    equipmentTag: "Equipment & Platform",
    equipmentTitle: "Equipment & Learning Platform",
    equipmentPlatformLabel: "Platform",
    equipmentPlatformValue: "Duplo (LEGO® Education)",
    equipmentCostLabel: "Reference equipment cost",
    equipmentCostValue: "approx. 1,000,000 VNĐ / kit",
    equipmentCostNote: "Note: This is a reference equipment cost subject to market variance — it is strictly not tuition.",
    equipmentComputerLabel: "Computer",
    equipmentComputerValue: "NOT required",
    equipmentTabletLabel: "Additional equipment",
    equipmentTabletValue: "A tablet may optionally be used for supplementary interactive activities — not mandatory for the Duplo program.",
    smallGroupTag: "Classroom Model",
    smallGroupTitle: "SMALL-GROUP ROBOTICS LEARNING",
    smallGroupIntro: "ROBOTO organizes small-group learning so every student gets ample hands-on time to build, test, program, and present their work.",
    smallGroupIdealBadge: "Ideal Model",
    smallGroupIdealTitle: "2 students / kit",
    smallGroupIdealDesc: "Maximizes direct hardware interaction and deepens one-on-one collaborative teamwork.",
    smallGroupIdealPoints: ["More hands-on building time for each individual learner", "Seamless peer collaboration with clear role rotation", "Deeper practical iteration through building and testing cycles"],
    smallGroupCostBadge: "Cost-Optimized Model",
    smallGroupCostTitle: "3 students / kit",
    smallGroupCostDesc: "Retains dynamic collaborative teamwork while optimizing hardware kit investment.",
    smallGroupCostPoints: ["Still fully group-based with active collaborative problem-solving", "Optimizes equipment expenditure per student", "Balances classroom operational and spatial efficiency"],
    roadmapTitle: "Position in Pathway",
    roadmapSubtitle: "The initial node across the comprehensive 10-level continuum from early childhood to global competition",
    currentBadge: "Current Node",
    skillsTitle: "Core Competencies Developed",
    skillsDescription: "Systematic progression balancing early physical engineering and spatial logical thinking:",
    firstCourseNotice: "First foundational course of the ROBOTO Robotics Pathway",
    nextStepCardTitle: "What's next for your child?",
    nextStepCardDesc: "Upon completing D1, children possess strong visual guide fluency and balance instincts. The natural progression is D2 – My City, expanding into municipal infrastructure networks and synchronized gear systems.",
    nextStepButton: "Explore D2 Course Details →",
    nextCourseLabel: "Next course along the pathway:",
    nextCourseButton: "D2 — My City →",
    skillsList: [{
      name: "Visual guide assembly",
      desc: "Deconstruct multi-step pictorial guides and identify 3D shapes."
    }, {
      name: "Structural stability",
      desc: "Discover wide support bases, interlocking brick patterns, and gravity balance."
    }, {
      name: "Rotational movement",
      desc: "Explore axle shafts, gears, and synchronized rotary motion."
    }, {
      name: "Rolling mechanics",
      desc: "Experiment with wheels, inclined ramps, and surface friction."
    }, {
      name: "Folding & Hinges",
      desc: "Construct barriers, swinging doors, and pivoting joints."
    }, {
      name: "Order & Sequencing",
      desc: "Structure physical processes with clear chronological prerequisites."
    }, {
      name: "Directional arrow cards",
      desc: "Translate real-world path planning into symbolic step commands."
    }, {
      name: "Repetition & Loops",
      desc: "Recognize repeating visual and kinematic patterns in mechanics."
    }, {
      name: "Simple debugging",
      desc: "Identify loose connections, misaligned gears, and test corrections calmly."
    }]
  }
};

// 10-course nodes roadmap with matched course thumbnail images
const pathwayNodes = [{
  code: "D1",
  path: "/RoboticsCourseD1Page",
  isCurrent: true,
  label: "D1",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png"
}, {
  code: "D2",
  path: "/RoboticsCourseD2Page",
  isCurrent: false,
  label: "D2",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png"
}, {
  code: "D3",
  path: "/RoboticsCourseD3Page",
  isCurrent: false,
  label: "D3",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png"
}, {
  code: "D4",
  path: "/RoboticsCourseD4Page",
  isCurrent: false,
  label: "D4",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png"
}, {
  code: "HP1",
  path: "/RoboticsCourseHP1Page",
  isCurrent: false,
  label: "HP1",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp"
}, {
  code: "HP2",
  path: "/RoboticsCourseHP2Page",
  isCurrent: false,
  label: "HP2",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png"
}, {
  code: "HP3",
  path: "/RoboticsCourseHP3Page",
  isCurrent: false,
  label: "HP3",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png"
}, {
  code: "HP4",
  path: "/RoboticsCourseHP4Page",
  isCurrent: false,
  label: "HP4",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png"
}, {
  code: "HP5",
  path: "/RoboticsCourseHp5Page",
  isCurrent: false,
  label: "HP5",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png"
}, {
  code: "HP6",
  path: "/RoboticsCourseHP6Page",
  isCurrent: false,
  label: "HP6",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png"
}];
export default function RoboticsCourseD1Detail() {
  const [lang, setLang] = React.useState<"vi" | "en">("vi");
  React.useEffect(() => {
    const readLang = () => {
      try {
        const stored = localStorage.getItem("roboto_lang");
        if (stored === "en" || stored === "vi") {
          setLang(stored);
        }
      } catch {
        // Storage access fallback
      }
    };
    readLang();
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: string;
      }>;
      const newLang = customEvent.detail?.lang;
      if (newLang === "en" || newLang === "vi") {
        setLang(newLang);
      } else {
        readLang();
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", readLang);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", readLang);
    };
  }, []);
  const t = contentData[lang];
  return <section id="roboticscoursed1detail" data-nav="light" className="relative bg-background text-foreground py-10 md:py-16 selection:bg-accent selection:text-accent-foreground">
      {/* Background soft ambient accents */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-24 right-1/4 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute top-1/2 -left-20 h-96 w-96 rounded-full bg-secondary/5 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 space-y-12">
        {/* (1) 4-Level Breadcrumb Navigation */}
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm font-medium border-b border-border pb-4">
          <ol className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-muted-foreground">
            <li>
              <Link to="/" className="hover:text-foreground transition-colors hover:underline">
                {t.breadcrumbHome}
              </Link>
            </li>
            <li aria-hidden="true">
              <ChevronRight className="size-3.5 text-muted-foreground/60" />
            </li>
            <li>
              <Link to="/RoboticsLearningPathwayPage" className="hover:text-foreground transition-colors hover:underline">
                {t.breadcrumbPathway}
              </Link>
            </li>
            <li aria-hidden="true">
              <ChevronRight className="size-3.5 text-muted-foreground/60" />
            </li>
            <li>
              <Link to="#" className="hover:text-foreground transition-colors hover:underline">
                {t.breadcrumbTrack}
              </Link>
            </li>
            <li aria-hidden="true">
              <ChevronRight className="size-3.5 text-muted-foreground/60" />
            </li>
            <li aria-current="page">
              <span className="text-foreground font-semibold">
                {t.breadcrumbCurrent}
              </span>
            </li>
          </ol>

          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 px-2.5 py-0.5 font-semibold tracking-wide">
            {t.pathwayBadge}
          </Badge>
        </nav>

        {/* Hero Visual: Duplo Construction Scene (Top Visual) */}
        <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-sm">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_d1_hero_building.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_d1_hero_building.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_d1_hero_building.png-wvc-sizes" alt="Illustrative LEGO Duplo basic structure assembly with large bricks" loading="lazy" className="w-full h-56 sm:h-80 md:h-[400px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/40 to-transparent flex items-end p-4 sm:p-6 md:p-8">
            <div className="bg-card/90 backdrop-blur-md border border-border/80 rounded-xl p-4 sm:p-5 max-w-xl shadow-sm">
              <p className="text-xs font-mono font-bold text-primary uppercase tracking-wider flex items-center gap-1.5">
                <Blocks className="size-4" />
                <span>{lang === "vi" ? "Minh họa mô hình D1" : "Illustrative D1 Construction"}</span>
              </p>
              <p className="text-sm sm:text-base font-serif font-bold text-foreground mt-1">
                {lang === "vi" ? "Thực hành xây dựng kết cấu chân đế rộng và khóa gạch Duplo" : "Basic Structure-Building with Oversized LEGO Duplo Bricks"}
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">
                {lang === "vi" ? "Mô phỏng quy trình học tập trực quan giúp trẻ nhận biết tỷ lệ hình học và giằng cơ học chịu lực." : "Illustrative technical representation of early childhood structural integrity and balance principles."}
              </p>
            </div>
          </div>
        </div>

        {/* Hero Course Header Card with Primary & Secondary CTAs */}
        <div className="rounded-2xl border border-border bg-card p-6 md:p-10 shadow-sm relative overflow-hidden">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 pb-8 border-b border-border">
            <div className="space-y-4 max-w-3xl">
              <div className="inline-flex items-center gap-3">
                <span className="inline-flex items-center justify-center size-12 rounded-xl bg-primary text-primary-foreground font-serif font-bold text-2xl shadow-xs">
                  {t.courseCode}
                </span>
                <span className="text-xs sm:text-sm font-semibold text-muted-foreground uppercase tracking-widest">
                  Robotics Foundation · Step 1
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground">
                {t.courseTitle}
              </h1>

              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                {t.description}
              </p>

              {/* Top Two CTAs: Primary Booking & Secondary Pathway */}
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-sm px-6">
                  <Link to="/FreeTrialContactPage">
                    <Sparkles className="size-4 mr-2" />
                    {t.ctaConsultation}
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground font-semibold px-6">
                  <Link to="/RoboticsLearningPathwayPage">
                    <Compass className="size-4 mr-2" />
                    {t.ctaPathway}
                  </Link>
                </Button>
              </div>
            </div>

            <div className="shrink-0 flex flex-col gap-2 bg-muted/40 p-4 rounded-xl border border-border/60 max-w-xs">
              <div className="flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
                <Sparkles className="size-4" />
                <span>{t.firstCourseNotice}</span>
              </div>
              <div className="text-xs text-muted-foreground leading-relaxed">
                {lang === "vi" ? "Dành cho trẻ lần đầu tiếp cận công nghệ & kỹ thuật lắp ráp mà không cần màn hình máy tính." : "Designed for young learners starting their first engineering path with tangible screen-free materials."}
              </div>
            </div>
          </div>

          {/* Key Metric Facts */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 pt-8">
            <div className="flex items-start gap-3.5 p-3.5 rounded-xl bg-muted/30 border border-border/50">
              <div className="size-9 rounded-lg bg-accent/15 text-accent-foreground flex items-center justify-center shrink-0">
                <Users className="size-4 text-accent" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">
                  {t.ageLabel}
                </p>
                <p className="text-base font-semibold text-foreground mt-0.5">
                  {t.ageValue}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3.5 p-3.5 rounded-xl bg-muted/30 border border-border/50">
              <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Blocks className="size-4" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">
                  {t.platformLabel}
                </p>
                <p className="text-base font-semibold text-foreground mt-0.5">
                  {t.platformValue}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3.5 p-3.5 rounded-xl bg-muted/30 border border-border/50">
              <div className="size-9 rounded-lg bg-secondary/20 text-secondary-foreground flex items-center justify-center shrink-0">
                <Clock className="size-4 text-secondary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">
                  {t.durationLabel}
                </p>
                <p className="text-base font-semibold text-foreground mt-0.5">
                  {t.durationValue}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3.5 p-3.5 rounded-xl bg-muted/30 border border-border/50">
              <div className="size-9 rounded-lg bg-muted text-foreground flex items-center justify-center shrink-0">
                <Wrench className="size-4" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">
                  {t.groupLabel}
                </p>
                <p className="text-base font-semibold text-foreground mt-0.5">
                  {t.groupValue}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Position in Pathway (Mini Roadmap with 10 clickable nodes + course thumbnails) */}
        <div className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
                <Target className="size-3.5" />
                <span>{t.roadmapTitle}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {t.roadmapSubtitle}
              </p>
            </div>
            <Badge className="bg-primary/10 text-primary border-primary/20 w-fit">
              D1 → D4 (4–6y) · HP1 → HP6 (7+y)
            </Badge>
          </div>

          <div className="overflow-x-auto pb-2">
            <div className="flex items-center gap-2.5 min-w-[780px]">
              {pathwayNodes.map((node, i) => <React.Fragment key={node.code}>
                  {node.isCurrent ? <div data-index={i} className="flex flex-col items-center justify-center min-w-[68px] p-2 rounded-xl bg-primary text-primary-foreground font-serif font-bold text-xs sm:text-sm shadow-sm ring-2 ring-primary/40 ring-offset-2 relative gap-1">
                      <img src={node.image} data-wvc-srcset={`${node.image}-wvc-srcset`} data-wvc-sizes={`${node.image}-wvc-sizes`} alt={`${node.code} robotics course thumbnail`} loading="lazy" className="size-7 object-cover rounded border border-primary-foreground/30 bg-background/20" />
                      <span>{node.code}</span>
                      <span className="text-[9px] font-sans font-medium uppercase tracking-tighter opacity-90">
                        Current
                      </span>
                    </div> : <Button asChild variant="outline" size="sm" className="min-w-[68px] h-auto p-2 rounded-xl border-border hover:border-primary hover:text-primary hover:bg-muted font-serif font-semibold text-xs flex flex-col items-center justify-center transition-colors gap-1">
                      <Link to={node.path} aria-label={`Go to course ${node.code}`}>
                        <img src={node.image} data-wvc-srcset={`${node.image}-wvc-srcset`} data-wvc-sizes={`${node.image}-wvc-sizes`} alt={`${node.code} robotics course thumbnail`} loading="lazy" className="size-7 object-cover rounded border border-border" />
                        <span>{node.code}</span>
                        <span className="text-[9px] font-sans font-normal text-muted-foreground">
                          {i < 4 ? "4–6y" : "7+y"}
                        </span>
                      </Link>
                    </Button>}

                  {i < pathwayNodes.length - 1 && <ChevronRight className="size-4 text-muted-foreground/40 shrink-0" />}
                </React.Fragment>)}
            </div>
          </div>
        </div>

        {/* 'Khóa học này dành cho ai?' / 'Who is this course for?' */}
        <div className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-6">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
              <HelpCircle className="size-3.5" />
              <span>Target Learners</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-foreground">
              {t.targetAudienceTitle}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground">
              {t.targetAudienceSubtitle}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <Badge variant="outline" className="border-secondary/40 text-secondary-foreground bg-secondary/15 font-semibold px-3 py-1 text-xs">
              {t.targetAudienceAge}
            </Badge>
            <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 font-semibold px-3 py-1 text-xs">
              {t.targetAudiencePrereq}
            </Badge>
          </div>

          <p className="text-sm sm:text-base text-foreground/85 leading-relaxed">
            {t.targetAudienceDesc}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
            {t.targetAudiencePoints.map((pt, idx) => <div key={idx} data-index={idx} className="p-4 rounded-xl bg-muted/40 border border-border/70 space-y-2">
                <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                  <CheckCircle2 className="size-4" />
                  <span>{pt.title}</span>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {pt.desc}
                </p>
              </div>)}
          </div>
        </div>

        {/* Mid-Page Technical Visual: Step-by-Step Duplo Structure Assembly */}
        <div className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="space-y-1">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
                <Layers className="size-3.5" />
                <span>{lang === "vi" ? "Quy trình lắp ráp thực hành" : "Assembly Step Sequence"}</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-serif font-bold text-foreground">
                {lang === "vi" ? "Quy trình từng bước xây dựng mô hình D1" : "Step-by-Step Basic Structure Assembly"}
              </h3>
            </div>
            <Badge variant="outline" className="border-secondary/40 text-secondary-foreground bg-secondary/15 font-semibold text-xs w-fit">
              {lang === "vi" ? "Đọc sơ đồ 2D → 3D" : "2D Blueprint to 3D"}
            </Badge>
          </div>

          <div className="relative rounded-xl overflow-hidden border border-border bg-muted/20">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_d1_step_building.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_d1_step_building.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_d1_step_building.png-wvc-sizes" alt="Step-by-step LEGO Duplo basic structure-building and assembly sequence" loading="lazy" className="w-full h-56 sm:h-72 md:h-80 object-cover" />
            <div className="bg-muted/60 border-t border-border p-4 text-xs sm:text-sm text-muted-foreground flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <span className="font-medium text-foreground">
                {lang === "vi" ? "4 bước chính: Phân loại khối → Ghép chân đế → Lắp giằng chịu lực → Thử nghiệm độ bền" : "4 Key Phases: Sort Bricks → Interlock Base → Mount Support Bracing → Balance Test"}
              </span>
              <span className="text-xs font-mono text-primary font-semibold shrink-0">
                LAB SPEC D1-STEPS
              </span>
            </div>
          </div>
        </div>

        {/* 12-session Structure Timeline */}
        <div className="space-y-6">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
              <Clock className="size-3.5" />
              <span>Session Roadmap</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-foreground">
              {t.timelineTitle}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground">
              {t.timelineSubtitle}
            </p>
          </div>

          <div className="space-y-4">
            {t.timelinePhases.map((phase, idx) => <Card key={idx} data-index={idx} className="border-border bg-card p-5 md:p-6 shadow-2xs hover:border-primary/30 transition-colors">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="md:w-1/3 space-y-1.5 shrink-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono font-bold px-2.5 py-0.5 rounded-md bg-primary text-primary-foreground">
                        {phase.phase}
                      </span>
                      <span className="text-xs font-medium text-muted-foreground">
                        {phase.sessions}
                      </span>
                    </div>
                    <h3 className="text-base sm:text-lg font-serif font-semibold text-foreground">
                      {phase.title}
                    </h3>
                    <p className="text-xs text-accent-foreground font-medium">
                      {phase.focus}
                    </p>
                  </div>

                  <div className="md:w-2/3 border-t md:border-t-0 md:border-l border-border md:pl-6 pt-3 md:pt-0">
                    <ul className="space-y-2 text-xs sm:text-sm text-muted-foreground">
                      {phase.items.map((it, itemIdx) => <li key={itemIdx} className="flex items-start gap-2">
                          <CheckCircle2 className="size-3.5 text-secondary shrink-0 mt-0.5" />
                          <span>{it}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>

        {/* 4-step Learning Methodology Visual Flow */}
        <div className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-6">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-secondary uppercase tracking-wider">
              <Sliders className="size-3.5 text-secondary" />
              <span>Pedagogy Architecture</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-foreground">
              {t.methodologyTitle}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground">
              {t.methodologySubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
            {t.methodologySteps.map((step, idx) => <div key={idx} data-index={idx} className="relative rounded-xl border border-border/80 bg-muted/20 p-5 space-y-3 hover:border-secondary/50 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono font-bold px-2 py-0.5 rounded-md bg-secondary/15 text-secondary-foreground">
                    Step {step.step}
                  </span>
                  {idx === 0 && <Rocket className="size-4 text-primary" />}
                  {idx === 1 && <Blocks className="size-4 text-secondary" />}
                  {idx === 2 && <RotateCw className="size-4 text-accent" />}
                  {idx === 3 && <Presentation className="size-4 text-foreground" />}
                </div>

                <h3 className="text-base font-serif font-bold text-foreground">
                  {step.title}
                </h3>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {step.desc}
                </p>
              </div>)}
          </div>
        </div>

        {/* Highlight Banner: Unplugged Programming Note */}
        <div className="rounded-xl border border-accent/40 bg-accent/10 p-5 md:p-6 flex flex-col sm:flex-row items-start gap-4">
          <div className="size-11 rounded-xl bg-accent text-accent-foreground flex items-center justify-center shrink-0 shadow-xs">
            <MonitorOff className="size-6" />
          </div>
          <div className="space-y-1">
            <h2 className="text-base sm:text-lg font-serif font-bold text-foreground flex items-center gap-2">
              <span>{t.unpluggedTitle}</span>
            </h2>
            <p className="text-sm text-foreground/85 leading-relaxed">
              {t.unpluggedNote}
            </p>
          </div>
        </div>

        {/* Technical Illustration Card: Structural Bracing Technique */}
        <div className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="space-y-1">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
                <Wrench className="size-3.5" />
                <span>{lang === "vi" ? "Kỹ thuật kết cấu cốt lõi" : "Core Structural Technique"}</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-serif font-bold text-foreground">
                {lang === "vi" ? "Nguyên lý chân đế rộng & Giằng chịu lực" : "Wide Support Base & Cross-Bracing Technique"}
              </h3>
            </div>
            <Badge variant="outline" className="border-secondary/40 text-secondary-foreground bg-secondary/15 font-semibold text-xs w-fit">
              {lang === "vi" ? "Trọng tâm D1" : "D1 Core Concept"}
            </Badge>
          </div>

          <div className="relative rounded-xl overflow-hidden border border-border bg-muted/20">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_d1_structural_bracing_technique.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_d1_structural_bracing_technique.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_d1_structural_bracing_technique.png-wvc-sizes" alt="Illustration of wide support base and cross-bracing technique for stable Duplo structures" loading="lazy" className="w-full h-56 sm:h-72 md:h-80 object-cover" />
            <div className="bg-muted/60 border-t border-border p-4 text-xs sm:text-sm text-muted-foreground flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <span className="font-medium text-foreground">
                {lang === "vi" ? "Cơ chế khóa gạch xen kẽ kết hợp giằng chéo giúp tăng gấp 3 lần khả năng chịu tải trọng trọng lực." : "Interlocking brick mechanics with cross-bracing triples resistance against vertical gravitational load."}
              </span>
              <span className="text-xs font-mono text-primary font-semibold shrink-0">
                LAB SPEC D1-BRACING
              </span>
            </div>
          </div>
        </div>

        {/* Section: Key Skills (9 skills as specified) */}
        <div className="space-y-6">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary tracking-wider uppercase">
              <Lightbulb className="size-3.5" />
              <span>Syllabus Breakdown</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-foreground">
              {t.skillsTitle}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground">
              {t.skillsDescription}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.skillsList.map((skill, index) => <Card key={index} data-index={index} className="border-border bg-card hover:border-primary/30 transition-colors shadow-2xs hover:shadow-sm">
                <CardHeader className="p-5 pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded-md bg-muted text-muted-foreground">
                      #0{index + 1}
                    </span>
                    {index < 5 ? <span className="text-xs text-primary font-medium flex items-center gap-1">
                        <Blocks className="size-3" /> Cơ học & Lắp ráp
                      </span> : <span className="text-xs text-accent-foreground font-medium flex items-center gap-1">
                        <RotateCw className="size-3 text-accent" /> Tư duy Thuật toán
                      </span>}
                  </div>
                  <CardTitle className="text-base font-serif font-semibold text-foreground pt-2">
                    {skill.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-5 pt-0">
                  <CardDescription className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {skill.desc}
                  </CardDescription>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* Grid: Final Project Card & Hardware Technical Spec Card */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Final Project Card: 'Ngày hội Kỹ sư nhí' */}
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-5">
            <div className="space-y-1">
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
                <Trophy className="size-3.5" />
                <span>{t.finalProjectTag}</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-serif font-bold text-foreground">
                {t.finalProjectTitle}
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                {t.finalProjectDesc}
              </p>
            </div>

            <div className="space-y-3 pt-1">
              {t.finalProjectStations.map((station, idx) => <div key={idx} data-index={idx} className="p-3.5 rounded-xl bg-muted/40 border border-border/70 space-y-1">
                  <p className="text-xs sm:text-sm font-semibold text-foreground flex items-center gap-2">
                    <span className="size-2 rounded-full bg-accent" />
                    {station.name}
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed pl-4">
                    {station.detail}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Technical Spec Card: 'Nền tảng / thiết bị' */}
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-5">
            <div className="space-y-1">
              <div className="inline-flex items-center gap-2 text-secondary uppercase tracking-wider">
                <Cpu className="size-3.5 text-secondary" />
                <span>{t.specTag}</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-serif font-bold text-foreground">
                {t.specTitle}
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground">
                LEGO® Duplo STEAM Standard Hardware Profile
              </p>
            </div>

            <div className="rounded-xl border border-border divide-y divide-border text-xs sm:text-sm">
              {t.specs.map((item, idx) => <div key={idx} data-index={idx} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 gap-1 bg-card hover:bg-muted/30 transition-colors">
                  <span className="font-medium text-muted-foreground">
                    {item.label}
                  </span>
                  <span className="font-semibold text-foreground text-left sm:text-right">
                    {item.value}
                  </span>
                </div>)}
            </div>
          </div>
        </div>

        {/* Equipment & Platform Card (with LEGO Duplo STEAM kit image) */}
        <div className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-6 shadow-sm">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider">
              <Cpu className="size-3.5 text-primary" />
              <span>{t.equipmentTag}</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-foreground">
              {t.equipmentTitle}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Platform Sub-Card with Kit Image */}
            <div className="p-4 rounded-xl bg-muted/30 border border-border/60 space-y-3">
              <div className="flex items-center gap-2 text-primary font-semibold text-xs uppercase tracking-wider">
                <Blocks className="size-4" />
                <span>{t.equipmentPlatformLabel}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png-wvc-sizes" alt="LEGO Duplo STEAM robotics kit used in ROBOTO's D1–D4 foundation track" loading="lazy" className="w-full sm:w-28 h-20 object-cover rounded-lg border border-border shrink-0" />
                <p className="text-base font-semibold text-foreground">
                  {t.equipmentPlatformValue}
                </p>
              </div>
            </div>

            {/* Computer Requirement */}
            <div className="p-4 rounded-xl bg-muted/30 border border-border/60 space-y-1.5">
              <div className="flex items-center gap-2 text-accent-foreground font-semibold text-xs uppercase tracking-wider">
                <MonitorOff className="size-4 text-accent" />
                <span>{t.equipmentComputerLabel}</span>
              </div>
              <p className="text-base font-semibold text-foreground">
                {t.equipmentComputerValue}
              </p>
            </div>

            {/* Reference Equipment Cost */}
            <div className="p-4 rounded-xl bg-muted/30 border border-border/60 space-y-2">
              <div className="flex items-center gap-2 text-secondary font-semibold text-xs uppercase tracking-wider">
                <Tag className="size-4 text-secondary" />
                <span>{t.equipmentCostLabel}</span>
              </div>
              <p className="text-base font-semibold text-foreground">
                {t.equipmentCostValue}
              </p>
              <div className="flex items-start gap-1.5 pt-1.5 border-t border-border/60 text-xs text-muted-foreground leading-relaxed">
                <Info className="size-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <span>{t.equipmentCostNote}</span>
              </div>
            </div>

            {/* Additional Equipment (Tablet) */}
            <div className="p-4 rounded-xl bg-muted/30 border border-border/60 space-y-2">
              <div className="flex items-center gap-2 text-primary font-semibold text-xs uppercase tracking-wider">
                <Tablet className="size-4 text-primary" />
                <span>{t.equipmentTabletLabel}</span>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                {t.equipmentTabletValue}
              </p>
            </div>
          </div>
        </div>

        {/* Small-Group Learning Model Section */}
        <div className="space-y-6">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-secondary uppercase tracking-wider">
              <Users className="size-3.5 text-secondary" />
              <span>{t.smallGroupTag}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-foreground">
              {t.smallGroupTitle}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-3xl">
              {t.smallGroupIntro}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Ideal Model: 2 students / kit */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm flex flex-col justify-between space-y-4 hover:border-primary/40 transition-colors">
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10 font-semibold px-2.5 py-0.5 text-xs">
                    {t.smallGroupIdealBadge}
                  </Badge>
                  <span className="text-xs font-mono font-medium text-muted-foreground">
                    1:2 Ratio
                  </span>
                </div>

                <h3 className="text-xl font-serif font-bold text-foreground">
                  {t.smallGroupIdealTitle}
                </h3>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {t.smallGroupIdealDesc}
                </p>

                <ul className="space-y-2 pt-2 border-t border-border">
                  {t.smallGroupIdealPoints.map((point, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                      <CheckCircle2 className="size-4 text-secondary shrink-0 mt-0.5" />
                      <span>{point}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            {/* Cost-Optimized Model: 3 students / kit */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm flex flex-col justify-between space-y-4 hover:border-secondary/50 transition-colors">
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <Badge variant="outline" className="border-secondary/50 text-secondary-foreground bg-secondary/15 font-semibold px-2.5 py-0.5 text-xs">
                    {t.smallGroupCostBadge}
                  </Badge>
                  <span className="text-xs font-mono font-medium text-muted-foreground">
                    1:3 Ratio
                  </span>
                </div>

                <h3 className="text-xl font-serif font-bold text-foreground">
                  {t.smallGroupCostTitle}
                </h3>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {t.smallGroupCostDesc}
                </p>

                <ul className="space-y-2 pt-2 border-t border-border">
                  {t.smallGroupCostPoints.map((point, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2 text-xs sm:text-sm text-muted-foreground">
                      <CheckCircle2 className="size-4 text-secondary shrink-0 mt-0.5" />
                      <span>{point}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 'Bước tiếp theo của con là gì?' Card */}
        <div className="rounded-2xl border border-secondary/40 bg-secondary/5 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-secondary uppercase tracking-wider">
              <GraduationCap className="size-4 text-secondary" />
              <span>Next Learning Milestone</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-serif font-bold text-foreground">
              {t.nextStepCardTitle}
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              {t.nextStepCardDesc}
            </p>
          </div>

          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-semibold shrink-0 w-full sm:w-auto">
            <Link to="/RoboticsCourseD2Page">
              {t.nextStepButton}
            </Link>
          </Button>
        </div>

        {/* Next Pathway Navigation & Actions Footer */}
        <Separator className="my-6" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-muted/30 p-6 md:p-8 rounded-2xl border border-border">
          <div className="space-y-1 text-center sm:text-left">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t.nextCourseLabel}
            </p>
            <h3 className="text-lg sm:text-xl font-serif font-bold text-foreground">
              D2 — {lang === "vi" ? "Thành phố của em" : "My City"} (5–6{" "}
              {lang === "vi" ? "tuổi" : "years"})
            </h3>
            <p className="text-xs text-muted-foreground">
              {lang === "vi" ? "Nâng cấp cơ cấu chuyển động liên hoàn và tư duy tổ hợp công trình." : "Advance into multi-gear mechanics and integrated town infrastructure."}
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center sm:justify-end gap-3 w-full sm:w-auto">
            <Button asChild variant="outline" size="lg" className="border-border hover:bg-muted font-medium w-full sm:w-auto">
              <Link to="/RoboticsLearningPathwayPage">
                <Compass className="size-4 mr-2" />
                {lang === "vi" ? "Xem toàn bộ lộ trình" : "Full Pathway"}
              </Link>
            </Button>

            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-semibold w-full sm:w-auto">
              <Link to="/RoboticsCourseD2Page">
                {t.nextCourseButton}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}